import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaAnalyticsGateway1Service } from '../service';
import { GetProvidersListIO } from '../types';

export const getGetProvidersListQueryKey = (req: GetProvidersListIO['input']) =>
  ['AnalyticsGatewayV1', 'GetProvidersList', req] as const;
type QueryKey = ReturnType<typeof getGetProvidersListQueryKey>;

/**
 * A hook that returns a query for the `GetProvidersList` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetProvidersListQuery = <E = unknown, D = GetProvidersListIO['output']>(
  req: GetProvidersListIO['input'],
  options?: LimitedSchemaQueryOptions<GetProvidersListIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetProvidersListIO['output'], E, D, QueryKey>({
    queryKey: getGetProvidersListQueryKey(req),
    queryFn: () => SchemaAnalyticsGateway1Service.GetProvidersList(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetProvidersList` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetProvidersListQueries = <E = unknown, D = GetProvidersListIO['output']>(
  argsArr: {
    req: GetProvidersListIO['input'];
    options?: LimitedSchemaQueryOptions<GetProvidersListIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetProvidersListQueryKey(req),
      queryFn: () => SchemaAnalyticsGateway1Service.GetProvidersList(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetProvidersListQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetProvidersList` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetProvidersList = () => {
  const queryClient = useQueryClient();

  const fetchGetProvidersList = async <E = unknown, D = GetProvidersListIO['output']>(
    req: GetProvidersListIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetProvidersListIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetProvidersListIO['output'], E, D, QueryKey>(
      getGetProvidersListQueryKey(req),
      () => SchemaAnalyticsGateway1Service.GetProvidersList(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetProvidersList;
};
